/**
 * Admin → Reported: the user-flagged spot review queue (under_review_spots).
 * Each row previews the spot + report metadata with full triage:
 *   - Restore  → re-persist into library_spots (clears the whole report stack)
 *   - Dismiss  → delete this report from the queue
 * Both confirm first; Restore touches protected library capital.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  dismissReportedSpot,
  getReportedSpots,
  restoreReportedSpot,
  type ReportedSpot,
} from "@/features/admin/lib/adminApi";
import { SimplifiedSpotCard } from "@/features/chat/components/SimplifiedSpotCard";
import type { SpotPayload } from "@/shared/models";
import { relativeTime } from "@/shared/format";

export function ReportedSpotsView() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-reported-spots"],
    queryFn: getReportedSpots,
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading queue…</p>;
  if (isError || !data)
    return <p className="text-sm text-danger">Couldn't load reported spots.</p>;
  if (data.length === 0)
    return (
      <p className="text-sm text-text-muted">
        Nothing in the review queue. 🎉
      </p>
    );

  return (
    <div className="space-y-5">
      {data.map((item) => (
        <ReportedRow key={item.id} item={item} />
      ))}
    </div>
  );
}

function ReportedRow({ item }: { item: ReportedSpot }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-reported-spots"] });
    qc.invalidateQueries({ queryKey: ["admin-overview"] });
  };

  const restore = useMutation({
    mutationFn: () => restoreReportedSpot(item.id),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: () => dismissReportedSpot(item.id),
    onSuccess: invalidate,
  });
  const busy = restore.isPending || dismiss.isPending;

  const badge = (
    <span className="text-[11px] rounded-full bg-danger/15 text-danger px-2 py-0.5 font-medium">
      {item.reportCount} report{item.reportCount === 1 ? "" : "s"}
    </span>
  );

  const actions = (
    <div className="space-y-3">
      <div className="text-xs text-text-muted space-y-1">
        {item.notes && (
          <p>
            <span className="text-text-muted/70">Note:</span> {item.notes}
          </p>
        )}
        <p className="text-text-muted/70">
          {item.drillTarget ?? "uncategorized"}
          {item.drillDifficulty ? ` · ${item.drillDifficulty}` : ""}
          {item.reportedAt ? ` · ${relativeTime(item.reportedAt)}` : ""}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || !item.restorable}
          title={
            item.restorable
              ? "Re-add this spot to the library"
              : "Missing drill metadata — can't restore"
          }
          onClick={() => {
            if (window.confirm("Restore this spot to the library?")) restore.mutate();
          }}
          className="flex-1 rounded-lg bg-accent/15 text-accent text-sm font-medium py-2 disabled:opacity-40 hover:bg-accent/25 transition"
        >
          {restore.isPending ? "Restoring…" : "Restore to library"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (window.confirm("Dismiss this report from the queue?")) dismiss.mutate();
          }}
          className="flex-1 rounded-lg border border-border text-text-muted text-sm font-medium py-2 disabled:opacity-40 hover:bg-text/5 hover:text-text transition"
        >
          {dismiss.isPending ? "Dismissing…" : "Dismiss"}
        </button>
      </div>
      {(restore.isError || dismiss.isError) && (
        <p className="text-xs text-danger">Action failed. Try again.</p>
      )}
    </div>
  );

  return (
    <SimplifiedSpotCard
      spot={item.spot as unknown as SpotPayload}
      topRight={badge}
      actions={actions}
    />
  );
}
